/**
 * PATH: packages/email-hub/src/feature/pages/inbox-page/controller/useInboxSidebarRuntime.ts
 * FILE_ID: EMAIL-INBOX-CONTROLLER-SIDEBAR-001
 * PURPOSE: Own keyboard runtime, sidebar projection, filter pills, and invites.
 */

/* eslint-disable react-hooks/exhaustive-deps -- Preserve pre-extraction hook dependencies. */

import { useCallback, useEffect, useMemo, useRef } from 'react'
import { Mail } from 'lucide-react'
import { useEmailAllocationMembers } from '../../../hooks/useEmailMembers'
import { useInboxHotkeys } from '../../../hooks/useInboxHotkeys'
import {
  type CategorySidebarConfig,
  type SidebarFilters,
} from '../../../components/FilterSidebar'
import { buildInboxFilterPills, removeSearchToken } from '../../../lib/inboxFilterPills'
import {
  CATEGORY_BAR_ORDER,
  CATEGORY_CONFIG,
  getCategoryConfig,
} from '../../../lib/categoryConfig'
import { siblingDuplicateIds } from '../../../lib/dedup'
import { emailPath } from '../../../lib/basePath'
import { recordInboxOrder } from '../../../lib/inboxOrder'
import { unmuteCategory } from '../../../stores/mutedCategoriesStore'
import { useEmailFilterStore } from '../../../stores/emailFilterStore'
import { newDraftId } from '../../../compose/composeDockStore'
import type { useInboxDataRuntime } from './useInboxDataRuntime'
import type { useInboxDerivedRuntime } from './useInboxDerivedRuntime'
import type { useInboxState } from './useInboxState'

type InboxState = ReturnType<typeof useInboxState>
type InboxData = ReturnType<typeof useInboxDataRuntime>
type InboxDerived = ReturnType<typeof useInboxDerivedRuntime>

export function useInboxSidebarRuntime(
  state: InboxState,
  data: InboxData,
  derived: InboxDerived,
) {
  const dedupedRef = useRef(derived.dedupedEmails)
  dedupedRef.current = derived.dedupedEmails
  useEffect(() => {
    recordInboxOrder(derived.dedupedEmails.map((email) => email.id))
  }, [derived.dedupedEmails])
  const selectedIdsRef = useRef(state.selectedIds)
  selectedIdsRef.current = state.selectedIds
  const scrollRowIntoView = useCallback((id: string) => {
    requestAnimationFrame(() => {
      const escapedId =
        typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
          ? CSS.escape(id)
          : id
      document
        .querySelector(`[data-email-id="${escapedId}"]`)
        ?.scrollIntoView({ block: 'nearest' })
    })
  }, [])
  const arrowReadTimerRef = useRef<number | null>(null)
  const markRowReadDebounced = useCallback((id: string) => {
    if (arrowReadTimerRef.current) {
      window.clearTimeout(arrowReadTimerRef.current)
    }
    arrowReadTimerRef.current = window.setTimeout(() => {
      const email = dedupedRef.current.find(
        (candidate) => candidate.id === id,
      )
      if (!email || email.is_read) return
      data.updateEmailLocal(id, { is_read: true })
      void data.markAsRead(id).catch(() => {})
      void data.syncDuplicates(
        siblingDuplicateIds(dedupedRef.current, [id]),
        'mark_read',
      )
    }, 900)
  }, [
    data.updateEmailLocal,
    data.markAsRead,
    data.syncDuplicates,
  ])
  useEffect(() => () => {
    if (arrowReadTimerRef.current) {
      window.clearTimeout(arrowReadTimerRef.current)
    }
  }, [])
  useInboxHotkeys({
    panelOpenRef: state.shortcutsPanelOpenRef,
    emailsRef: dedupedRef,
    selectedEmailId: state.selectedEmailId,
    setSelectedEmailId: state.setSelectedEmailId,
    selectedIdsRef,
    setSelectedIds: state.setSelectedIds,
    arrowReadTimerRef,
    scrollRowIntoView,
    markRowReadDebounced,
    onOpenEmail: (id) => state.navigate(emailPath(`/${id}`)),
    onCompose: () =>
      state.navigate(
        `${emailPath('/compose')}?draft=${encodeURIComponent(newDraftId())}`,
      ),
    onToggleShortcutsPanel: () =>
      state.setShortcutsPanelOpen((open) => !open),
    onOpenQuickTriage: (emailId) => {
      state.setQuickTriageEmailId(emailId)
      state.setSelectedEmailId(emailId)
    },
    onAllocateToBoard: (emailId) => {
      state.setAllocateBoardEmailId(emailId)
      state.setSelectedEmailId(emailId)
    },
    bulkAction: data.bulkAction,
    archive: data.archive,
    toggleStar: data.toggleStar,
    toggleRead: data.toggleRead,
    updateEmailLocal: data.updateEmailLocal,
    removeEmailsLocal: data.removeEmailsLocal,
    refetch: data.refetch,
    toast: data.toast,
    syncDuplicates: data.syncDuplicates,
    setCardPriority: (cardId, priority) =>
      state.kanbanApi.updateCard(cardId, { priority }),
  })

  const sidebarCategories: CategorySidebarConfig[] = useMemo(
    () => CATEGORY_BAR_ORDER.map((key) => {
      const config = CATEGORY_CONFIG[key]
      return config
        ? { key, label: config.label, icon: config.icon }
        : { key, label: key, icon: Mail }
    }),
    [],
  )
  const sidebarFilters: SidebarFilters = {
    category:
      state.selectedCategories.length === 1
        ? state.selectedCategories[0]
        : undefined,
    isRead: state.isReadFilter,
    isStarred: state.isStarredFilter,
    jobIds: state.jobIdsIn,
    mailboxKeys: state.mailboxKeysExplicit,
    fromAddressContains: state.fromAddressContains,
    receivedAtGte: state.receivedAtGte,
    receivedAtLte: state.receivedAtLte,
    hasAttachments: state.hasAttachments,
    hasCalendarEvent: state.hasCalendarEvent,
  }
  const patchFilters = (patch: Partial<SidebarFilters>) => {
    if ('category' in patch) {
      state.setSelectedCategories(patch.category ? [patch.category] : [])
    }
    if ('isRead' in patch) state.setIsReadFilter(patch.isRead)
    if ('isStarred' in patch) state.setIsStarredFilter(patch.isStarred)
    if ('jobIds' in patch) state.setJobIdsIn(patch.jobIds || [])
    if ('mailboxKeys' in patch) {
      state.setMailboxKeysExplicit(patch.mailboxKeys || [])
    }
    if ('fromAddressContains' in patch) {
      state.setFromAddressContains(patch.fromAddressContains || '')
    }
    if ('receivedAtGte' in patch) {
      state.setReceivedAtGte(patch.receivedAtGte)
    }
    if ('receivedAtLte' in patch) {
      state.setReceivedAtLte(patch.receivedAtLte)
    }
    if ('hasAttachments' in patch) {
      state.setHasAttachments(patch.hasAttachments)
    }
    if ('hasCalendarEvent' in patch) {
      state.setHasCalendarEvent(patch.hasCalendarEvent)
    }
  }
  const applyPreset = (raw: Record<string, unknown>) => {
    state.setSelectedCategories(
      Array.isArray(raw.categories)
        ? raw.categories.filter(
            (category): category is string => typeof category === 'string',
          )
        : typeof raw.category === 'string' && raw.category
          ? [raw.category]
          : [],
    )
    state.setIsReadFilter(
      typeof raw.isRead === 'boolean' ? raw.isRead : undefined,
    )
    state.setIsStarredFilter(
      typeof raw.isStarred === 'boolean' ? raw.isStarred : undefined,
    )
    state.setJobIdsIn(
      Array.isArray(raw.jobIds)
        ? raw.jobIds.filter(
            (jobId): jobId is number => typeof jobId === 'number',
          )
        : [],
    )
    state.setMailboxKeysExplicit(
      Array.isArray(raw.mailboxKeys)
        ? raw.mailboxKeys.filter(
            (key): key is string => typeof key === 'string',
          )
        : Array.isArray(raw.syncedByUserIds)
          ? raw.syncedByUserIds
              .filter((id): id is number => typeof id === 'number')
              .map((id) => `u:${id}`)
          : [],
    )
    state.setFromAddressContains(
      typeof raw.fromAddressContains === 'string'
        ? raw.fromAddressContains
        : '',
    )
    state.setReceivedAtGte(
      typeof raw.receivedAtGte === 'string'
        ? raw.receivedAtGte
        : undefined,
    )
    state.setReceivedAtLte(
      typeof raw.receivedAtLte === 'string'
        ? raw.receivedAtLte
        : undefined,
    )
    state.setHasAttachments(
      typeof raw.hasAttachments === 'boolean'
        ? raw.hasAttachments
        : undefined,
    )
    state.setHasCalendarEvent(
      typeof raw.hasCalendarEvent === 'boolean'
        ? raw.hasCalendarEvent
        : undefined,
    )
  }
  const activeFilterCount = [
    state.selectedCategories.length > 0,
    state.isReadFilter !== undefined,
    state.isStarredFilter,
    state.allocationFilter,
    state.jobIdFilter,
    state.jobIdsIn.length > 0,
    state.mailboxKeysExplicit.length > 0,
    state.fromAddressContains,
    state.companyDomain,
    state.receivedAtGte || state.receivedAtLte,
    state.hasAttachments !== undefined,
    state.hasCalendarEvent !== undefined,
  ].filter(Boolean).length
  const hasActiveQuery =
    activeFilterCount > 0 || !!state.debouncedSearchQuery
  const showAllMailboxes = useEmailFilterStore(
    (store) => store.showAllMailboxes,
  )
  const filterPills = useMemo(() => {
    const tokenByKey = new Map(
      state.parsedDebouncedSearch.recognisedOperators.map(
        (token) => [token.key, token],
      ),
    )
    return buildInboxFilterPills({
      selectedCategories: state.selectedCategories,
      categoryLabel: (key) => getCategoryConfig(key).label,
      isReadFilter: state.isReadFilter,
      isStarredFilter: state.isStarredFilter,
      allocationFilter: state.allocationFilter,
      jobIdFilter: state.jobIdFilter,
      jobIdsIn: state.jobIdsIn,
      fromAddressContains: state.fromAddressContains,
      companyDomain: state.companyDomain,
      receivedAtGte: state.receivedAtGte,
      receivedAtLte: state.receivedAtLte,
      hasAttachments: state.hasAttachments,
      hasCalendarEvent: state.hasCalendarEvent,
      mailboxKeysExplicit: state.mailboxKeysExplicit,
      hiddenMailboxCount: state.hiddenMailboxes.size,
      mutedCategoryCount: state.mutedCategories.length,
      searchTokens: Array.from(tokenByKey.values()),
    })
  }, [
    state.parsedDebouncedSearch, state.selectedCategories, state.isReadFilter,
    state.isStarredFilter, state.allocationFilter, state.jobIdFilter,
    state.jobIdsIn, state.fromAddressContains, state.companyDomain,
    state.receivedAtGte, state.receivedAtLte, state.hasAttachments,
    state.hasCalendarEvent, state.mailboxKeysExplicit, state.hiddenMailboxes,
    state.mutedCategories,
  ])
  const removeFilterPill = (id: string) => {
    if (id.startsWith('cat:')) return state.toggleCategory(id.slice(4))
    if (id.startsWith('token:')) {
      return state.setSearchQuery(
        removeSearchToken(
          state.searchQuery,
          id.slice(6) as Parameters<typeof removeSearchToken>[1],
        ),
      )
    }
    switch (id) {
      case 'read': return state.setIsReadFilter(undefined)
      case 'starred': return state.setIsStarredFilter(undefined)
      case 'allocation': return state.setAllocationFilter(undefined)
      case 'job': return state.setJobIdFilter(undefined)
      case 'jobs-in': return state.setJobIdsIn([])
      case 'from': return state.setFromAddressContains('')
      case 'company': return state.setCompanyDomain('')
      case 'date':
        state.setReceivedAtGte(undefined)
        state.setReceivedAtLte(undefined)
        return
      case 'attachments': return state.setHasAttachments(undefined)
      case 'calendar': return state.setHasCalendarEvent(undefined)
      case 'mailboxes': return state.setMailboxKeysExplicit([])
      case 'mailboxes-hidden': return showAllMailboxes()
      case 'muted':
        for (const key of state.mutedCategories) unmuteCategory(key)
    }
  }
  const inviteMembers = useEmailAllocationMembers()

  return {
    sidebarCategories, sidebarFilters, patchFilters, applyPreset,
    activeFilterCount, hasActiveQuery, filterPills, removeFilterPill,
    inviteMembers,
  }
}
